/// REDUX ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
/// REACT ///
import { useEffect } from "react";
import { useState } from "react";
import { ReactNode } from "react";
/// ACTIONS ///
import { updateQuote } from "api/actions/quotes/quote";
/// TYPES ///
import { Quote } from "api/types/quote";
/// MUI ///
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
/// COMPONENTS ///
import StoreMeasurements from "../measurements";
import StoreAngles from "../angles";
import StoreEdges from "../edges";
import StoreAppliances from "../appliances";
import SketchActions from "components/quote/buttons/actions";
import StoreColorOptions from "../color_options";
import StoreQuoteDetails from "../quote_details";
import HistorySnackbar from "components/universal/history_snackbar";
import CreateStoreLabel from "components/store_quote/quote/dialog/label/create";
import Loading from "components/universal/loading";
import StoreSlabs from "../slabs";
/// FM ///
import { motion } from "framer-motion";
import { useAnimationControls } from "framer-motion";
/// STYLES ///
import { miniDrawerWidth } from "styles/drawer";
import { tab_underline_style } from "styles/sketch";
import { sketch_tab_btn } from "styles/sketch";
import { sketch_btn_anim } from "styles/sketch";
/// MOTION ///
import { measurement_motion } from "motion/sketch";
import { angles_motion } from "motion/sketch";
import { edges_motion } from "motion/sketch";
import { appliances_motion } from "motion/sketch";
import { material_motion } from "motion/sketch";
import { slabs_motion } from "motion/sketch";
import { quote_motion } from "motion/sketch";

interface TabPanelProps {
  children?: ReactNode;
  dir?: string;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

interface Props {
  quote: Quote,
  redo: () => void,
  undo: () => void,
  current_area: number,
  setCurrentArea: (area: number) => void
}

export default function StoreQuoteTabs({ quote, redo, undo, current_area, setCurrentArea }: Props) {
  const controls = useAnimationControls();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [index, setIndex] = useState(0);
  const [zoom, setZoom] = useState<number>(quote.zoom ? quote.zoom / 10 : 0.8);
  const [label_open, setLabelOpen] = useState<boolean>(false);
  const { alerts } = useAppSelector(state => state.history);
  const { updating: sketch_updating } = useAppSelector(state => state.sketch);
  const { updating: quote_updating } = useAppSelector(state => state.quote);
  const { areas_loaded } = useAppSelector(state => state.quote);
  const { quote_loaded } = useAppSelector(state => state.quote);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [copying, setCopying] = useState<boolean>(false);


  useEffect(() => {
    setCopying(false);
  }, [index]);

  useEffect(() => {
    const html = document.querySelector("html");
    if (html && index < 4) {
      html.style.overflow = "hidden";
    }
    return () => {
      const html = document.querySelector("html");
      if (html) {
        html.style.overflow = "visible";
      }
    }
  }, [index]);

  const zoomIn = () => {
    setZoom(prev => prev < 1.6 ? Math.round((prev + 0.1) * 10) / 10 : prev);
    dispatch(updateQuote({ zoom: zoom < 1.6 ? Math.round((zoom + 0.1) * 10) : zoom * 10 }, quote.id, false, true));
  }

  const zoomOut = () => {
    setZoom(prev => prev > 0.1 ? Math.round((prev - 0.1) * 10) / 10 : prev);
    dispatch(updateQuote({ zoom: zoom > 0.1 ? Math.round((zoom - 0.1) * 10) : zoom * 10 }, quote.id, false, true));
  }

  const changeTab = (index: number) => {
    setIndex(index);
    switch (index) {
      case 0:
        controls.start("measurement");
        break;
      case 1:
        controls.start("angles");
        break;
      case 2:
        controls.start("edges");
        break;
      case 3:
        controls.start("appliances");
        break;
      case 4:
        controls.start("material");
        break;
      case 5:
        controls.start("slabs");
        break;
      case 6:
        controls.start("quote");
        break;
    }
  }



  return (
    <Box sx={{
      position: "absolute",
      top: 50,
      left: `${miniDrawerWidth}px`,
      right: 0,
      bottom: 0,
      bgcolor: "background.paper",
    }}>
      <Stack direction="row" justifyContent="space-between">
        <motion.button style={sketch_tab_btn} whileHover={sketch_btn_anim} onClick={() => changeTab(0)}>
          Measurements
          <motion.div initial="measurement" variants={measurement_motion} animate={controls} style={tab_underline_style}></motion.div>
        </motion.button>
        <motion.button style={sketch_tab_btn} whileHover={sketch_btn_anim} onClick={() => changeTab(1)}>
          Angles
          <motion.div initial="measurement" variants={angles_motion} animate={controls} style={tab_underline_style}></motion.div>
        </motion.button>
        <motion.button style={sketch_tab_btn} whileHover={sketch_btn_anim} onClick={() => changeTab(2)}>
          Edges
          <motion.div initial="measurement" variants={edges_motion} animate={controls} style={tab_underline_style}></motion.div>
        </motion.button>
        <motion.button style={sketch_tab_btn} whileHover={sketch_btn_anim} onClick={() => changeTab(3)}>
          Appliances
          <motion.div initial="measurement" variants={appliances_motion} animate={controls} style={tab_underline_style}></motion.div>
        </motion.button>
        <motion.button style={sketch_tab_btn} whileHover={sketch_btn_anim} onClick={() => changeTab(4)}>
          Material
          <motion.div initial="measurement" variants={material_motion} animate={controls} style={tab_underline_style}></motion.div>
        </motion.button>
        <motion.button style={sketch_tab_btn} whileHover={sketch_btn_anim} onClick={() => changeTab(5)}>
          Slabs
          <motion.div initial="measurement" variants={slabs_motion} animate={controls} style={tab_underline_style}></motion.div>
        </motion.button>
        <motion.button style={sketch_tab_btn} whileHover={sketch_btn_anim} onClick={() => changeTab(6)}>
          Quote
          <motion.div initial="measurement" variants={quote_motion} animate={controls} style={tab_underline_style}></motion.div>
        </motion.button>
      </Stack>
      <TabPanel value={index} index={0} dir={theme.direction}>
        <StoreMeasurements
          index={index}
          zoom={zoom}
          setDisabled={setDisabled}
          current_area={current_area}
          setCurrentArea={setCurrentArea}
          copying={copying}
          setCopying={setCopying} />
      </TabPanel>
      <TabPanel value={index} index={1} dir={theme.direction}>
        <StoreAngles index={index} zoom={zoom} current_area={current_area} setCurrentArea={setCurrentArea} copying={copying} setCopying={setCopying} />
      </TabPanel>
      <TabPanel value={index} index={2} dir={theme.direction}>
        <StoreEdges index={index} zoom={zoom} current_area={current_area} setCurrentArea={setCurrentArea} copying={copying} setCopying={setCopying} />
      </TabPanel>
      <TabPanel value={index} index={3} dir={theme.direction}>
        <StoreAppliances index={index} zoom={zoom} current_area={current_area} setCurrentArea={setCurrentArea} copying={copying} setCopying={setCopying} />
      </TabPanel>
      <TabPanel value={index} index={4} dir={theme.direction}>
        <StoreColorOptions current_area={current_area} setCurrentArea={setCurrentArea} />
      </TabPanel>
      <TabPanel value={index} index={5} dir={theme.direction}>
        <StoreSlabs index={index} current_area={current_area} setCurrentArea={setCurrentArea} />
      </TabPanel>
      <TabPanel value={index} index={6} dir={theme.direction}>
        <StoreQuoteDetails index={index} quote={quote} />
      </TabPanel>
      {
        index < 4 ?
          <SketchActions
            disabled={disabled}
            loaded={quote_loaded && areas_loaded}
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            openLabel={() => setLabelOpen(true)}
            redo={redo}
            undo={undo}
            copying={copying}
            toggleCopy={(() => setCopying(prev => !prev))} /> :
          null
      }
      {
        alerts.map((alert, index) => <HistorySnackbar index={index} active={alert.active} message={alert.desc} />)
      }
      <Loading loading={sketch_updating || quote_updating} />
      <CreateStoreLabel open={label_open} setOpen={setLabelOpen} current_area={current_area} />
    </Box>
  );
}
